/**
 * OpsHomePage — role-aware dashboard (functional roles, no personal names).
 * - Executive Director: full oversight — KPIs, pipeline, delivery rate,
 *     overdue, team utilisation, pending approvals.
 * - Operations Manager: inbox pulse + all projects follow-up + SLA alerts.
 * - Track Leader: their track projects + tasks + team.
 * - Member: their assigned tasks only.
 * Route: /ops
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  opsApi,
  useOpsAuth,
  ROLE_LABELS_AR,
  isExecutiveDirector,
  isOperationsManager,
  isTrackLeader,
  isMember as isMemberRole,
} from './OpsAuthContext';
import OpsLayout from './OpsLayout';
import { PROJECT_STATUS, TASK_STATUS, fmtDate } from './opsConstants';

const StatCard = ({ label, value, color = '#1F3D2B', testId, hint }) => (
  <div
    data-testid={testId}
    className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-5"
  >
    <p className="text-[11px] font-['Noto_Kufi_Arabic'] text-[#7A866E]">{label}</p>
    <p
      className="font-['Playfair_Display'] text-3xl font-semibold mt-2 leading-none"
      style={{ color }}
    >
      {value}
    </p>
    {hint && (
      <p className="text-[10.5px] font-['Noto_Kufi_Arabic'] text-[#7A866E] mt-2">
        {hint}
      </p>
    )}
  </div>
);

const formatSAR = (n) => {
  if (!n) return '0';
  return new Intl.NumberFormat('ar-SA', { maximumFractionDigits: 0 }).format(n);
};

const OpsHomePage = () => {
  const { user } = useOpsAuth();
  const [inbox, setInbox] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectCounts, setProjectCounts] = useState({});
  const [myTasks, setMyTasks] = useState([]);
  const [team, setTeam] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);

  const isDirector = isExecutiveDirector(user);
  const isOps = isOperationsManager(user);
  const isLeader = isTrackLeader(user);
  const isMember = isMemberRole(user);

  useEffect(() => {
    if (isDirector || isOps) {
      opsApi.get('/inbox').then((r) => setInbox(r.data.counts)).catch(() => {});
    }
    if (isDirector || isOps || isLeader) {
      opsApi.get('/projects').then((r) => {
        setProjects(r.data.projects);
        setProjectCounts(r.data.counts);
      }).catch(() => {});
    }
    if (!isOps) {
      opsApi.get('/tasks/mine').then((r) => setMyTasks(r.data)).catch(() => {});
    }
    if (isDirector) {
      opsApi.get('/users').then((r) => setTeam(r.data)).catch(() => {});
      opsApi.get('/approvals').then((r) => setPendingApprovals(r.data)).catch(() => {});
    }
  }, [isDirector, isOps, isLeader, isMember]);

  // --- Derived KPIs for Executive Director ---
  const kpis = useMemo(() => {
    if (!projects.length) return null;
    const now = new Date();
    const total = projects.length;
    const delivered = projects.filter((p) => p.status === 'delivered' || p.status === 'closed').length;
    const active = projects.filter((p) => ['active', 'discovery', 'proposal', 'review'].includes(p.status)).length;
    const overdue = projects.filter((p) => {
      if (!p.due_date) return false;
      if (['delivered', 'closed', 'cancelled'].includes(p.status)) return false;
      return new Date(p.due_date) < now;
    }).length;
    const revenue = projects
      .filter((p) => ['delivered', 'closed'].includes(p.status))
      .reduce((sum, p) => sum + (p.contract_value || 0), 0);
    const pipeline = projects
      .filter((p) => !['delivered', 'closed', 'cancelled'].includes(p.status))
      .reduce((sum, p) => sum + (p.contract_value || 0), 0);
    const deliveryRate = total > 0 ? Math.round((delivered / total) * 100) : 0;
    return { total, delivered, active, overdue, revenue, pipeline, deliveryRate };
  }, [projects]);

  const utilisation = useMemo(() => {
    if (!team.length) return [];
    return team.map((u) => {
      const assigned = projects.filter(
        (p) => p.lead_email === u.email || (p.member_emails || []).includes(u.email),
      );
      const activeAssigned = assigned.filter(
        (p) => !['delivered', 'closed', 'cancelled'].includes(p.status),
      );
      return { user: u, total: assigned.length, active: activeAssigned.length };
    });
  }, [team, projects]);

  return (
    <OpsLayout
      title={`مرحبًا ${user.name_ar}`}
      subtitle={`${ROLE_LABELS_AR[user.role]} · Childhood Horizons`}
    >
      {/* ============ EXECUTIVE DIRECTOR: KPI DASHBOARD ============ */}
      {isDirector && kpis && (
        <>
          {/* Top-line financial KPIs */}
          <section className="grid md:grid-cols-4 gap-4 mb-4">
            <StatCard
              label="الإيرادات المُحقّقة (ريال)"
              value={formatSAR(kpis.revenue)}
              color="#1F3D2B"
              hint="مشاريع مُسلَّمة/مُغلقة"
              testId="kpi-revenue"
            />
            <StatCard
              label="الإيرادات المتوقعة (Pipeline)"
              value={formatSAR(kpis.pipeline)}
              color="#4A5D23"
              hint="مشاريع نشطة/تحت التنفيذ"
              testId="kpi-pipeline"
            />
            <StatCard
              label="معدل التسليم"
              value={`${kpis.deliveryRate}%`}
              color="#265855"
              hint={`${kpis.delivered} من ${kpis.total} مشاريع`}
              testId="kpi-delivery-rate"
            />
            <StatCard
              label="مشاريع متأخرة"
              value={kpis.overdue}
              color="#7A2E22"
              hint="تجاوزت الموعد النهائي"
              testId="kpi-overdue"
            />
          </section>

          {/* Operational KPIs */}
          <section className="grid md:grid-cols-4 gap-4 mb-6">
            <Link to="/ops/inbox" data-testid="director-inbox-widget">
              <StatCard label="طلبات جديدة في الإنبوكس" value={inbox?.new || 0} color="#294B7B" />
            </Link>
            <StatCard label="مشاريع نشطة" value={kpis.active} color="#3F5021" testId="kpi-active" />
            <Link to="/ops/approvals" data-testid="director-approvals-widget">
              <StatCard
                label="طلبات اعتماد التسليم"
                value={pendingApprovals.length}
                color="#8A6A18"
                hint={pendingApprovals.length ? 'بانتظار قرارك' : 'لا شيء'}
                testId="kpi-approvals"
              />
            </Link>
            <StatCard label="أعضاء الفريق" value={team.length} color="#573F7A" />
          </section>

          {/* Team Utilisation */}
          {utilisation.length > 0 && (
            <section
              data-testid="team-utilisation"
              className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-6 mb-6"
            >
              <h3 className="font-['Noto_Kufi_Arabic'] text-[16px] font-bold text-[#1F3D2B] mb-4">
                استخدام الفريق (Team Utilisation)
              </h3>
              <div className="grid md:grid-cols-3 gap-3">
                {utilisation.map((row) => (
                  <div key={row.user.id} className="border border-[#EFEAE1] rounded-xl p-4">
                    <p className="font-['Noto_Kufi_Arabic'] text-[13px] font-semibold text-[#1F3D2B]">
                      {row.user.name_ar}
                    </p>
                    <p className="font-['Noto_Kufi_Arabic'] text-[10.5px] text-[#7A866E] mt-1">
                      {ROLE_LABELS_AR[row.user.role]}
                    </p>
                    <div className="flex items-baseline gap-3 mt-3">
                      <span className="font-['Playfair_Display'] text-2xl text-[#3F5021]">
                        {row.active}
                      </span>
                      <span className="text-[11px] font-['Noto_Kufi_Arabic'] text-[#7A866E]">
                        نشط · {row.total} إجمالي
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* ============ OPERATIONS MANAGER OVERVIEW ============ */}
      {isOps && inbox && (
        <section className="grid md:grid-cols-4 gap-4 mb-6">
          <Link to="/ops/inbox" data-testid="ops-inbox-widget">
            <StatCard label="طلبات جديدة" value={inbox.new} color="#294B7B" testId="stat-inbox-new" />
          </Link>
          <StatCard label="قيد المراجعة" value={inbox.in_review || 0} color="#8A6A18" />
          <StatCard label="مشاريع نشطة" value={projectCounts.active || 0} color="#3F5021" testId="stat-active" />
          <StatCard label="مُسلَّم هذا الشهر" value={projectCounts.delivered || 0} color="#1F3D2B" />
        </section>
      )}

      {/* ============ RECENT PROJECTS (director / ops / leader) ============ */}
      {!isMember && projects.length > 0 && (
        <section className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-['Noto_Kufi_Arabic'] text-[16px] font-bold text-[#1F3D2B]">
              {isLeader ? 'مشاريع مساري' : 'أحدث المشاريع'}
            </h3>
            <Link
              to="/ops/projects"
              className="text-[12px] font-['Noto_Kufi_Arabic'] text-[#4A5D23] hover:underline"
            >
              عرض الكل ({projects.length})
            </Link>
          </div>
          <ul className="divide-y divide-[#EFEAE1]">
            {projects.slice(0, 5).map((p) => (
              <li key={p.id}>
                <Link
                  to={`/ops/projects/${p.id}`}
                  className="flex items-center justify-between py-3 hover:bg-[#FAFAF7] rounded-lg px-2"
                  data-testid={`home-project-${p.code}`}
                >
                  <div className="min-w-0">
                    <p className="font-['Noto_Kufi_Arabic'] text-[13px] font-semibold text-[#1F3D2B]">
                      {p.code} · {p.client_name}
                    </p>
                    <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#7A866E] mt-0.5">
                      {p.service_type} · تحديث {fmtDate(p.updated_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-[10px] font-['Noto_Kufi_Arabic'] font-semibold px-2 py-0.5 rounded-full ${PROJECT_STATUS[p.status]?.color}`}>
                      {PROJECT_STATUS[p.status]?.label}
                    </span>
                    <span className="font-['Playfair_Display'] text-[15px] text-[#1F3D2B]">
                      {p.progress || 0}%
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ============ MY TASKS (all except Operations Manager) ============ */}
      {!isOps && (
        <section
          data-testid="home-mytasks"
          className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-['Noto_Kufi_Arabic'] text-[16px] font-bold text-[#1F3D2B]">
              مهامي ({myTasks.filter((t) => t.status !== 'done').length} مفتوحة)
            </h3>
            <Link
              to="/ops/my-tasks"
              className="text-[12px] font-['Noto_Kufi_Arabic'] text-[#4A5D23] hover:underline"
            >
              عرض الكل
            </Link>
          </div>
          {myTasks.length === 0 ? (
            <p className="font-['Noto_Kufi_Arabic'] text-[13px] text-[#7A866E] text-center py-6">
              لا توجد مهام مسندة إليكِ حاليًا
            </p>
          ) : (
            <ul className="divide-y divide-[#EFEAE1]">
              {myTasks.filter((t) => t.status !== 'done').slice(0, 5).map((t) => (
                <li key={t.id} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-['Noto_Kufi_Arabic'] text-[13px] font-semibold text-[#1F3D2B]">
                        {t.title}
                      </p>
                      {t.project && (
                        <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#7A866E] mt-0.5">
                          {t.project.code} · {t.project.client_name}
                        </p>
                      )}
                    </div>
                    <span className={`shrink-0 text-[10px] font-['Noto_Kufi_Arabic'] font-semibold px-2 py-0.5 rounded-full ${TASK_STATUS[t.status]?.color}`}>
                      {TASK_STATUS[t.status]?.label}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </OpsLayout>
  );
};

export default OpsHomePage;
